import appConfigStoreModule from "@core/@app-config/appConfigStoreModule";
import Vue from "vue";
import Vuex from "vuex";
import app from "./app";
import VuexPersistence from "vuex-persist";
import CryptoJS from "crypto-js";

import auth from "./modules/auth";
import showProformModal from "./modules/showProformModal";
import showZelleModal from "./modules/showZelleModal";
import showCarouselModal from "./modules/showCarouselModal";
import showInvoiceAdModal from "./modules/showInvoiceAdModal";
import showInvoiceAdModalDefaultState from "./modules/showInvoiceAdModal/state";
import showInvoiceModuleOnboardingModal from "./modules/showInvoiceModuleOnboardingModal";
import showInvoiceModuleOnboardingModalDefaultState from "./modules/showInvoiceModuleOnboardingModal/state";
import showRentalsModal from "./modules/showRentalsModal";
import dashboard from "./modules/dashboard";
import findYourTruck from "./modules/findYourTruck";

Vue.use(Vuex);

const ENCRYPTION_KEY = process.env.VUE_APP_ENCRYPTION_KEY;
const ENCRYPT_STORAGE = process.env.VUE_APP_ENCRYPT_STORAGE === "true";

function sanitizePersistedState(state = {}) {
  return {
    auth: {
      token: state?.auth?.token ?? null,
      lang: state?.auth?.lang ?? "es",
    },
    showProformModal: state?.showProformModal,
    showZelleModal: state?.showZelleModal,
    showCarouselModal: state?.showCarouselModal,
    showInvoiceAdModal: {
      ...showInvoiceAdModalDefaultState,
      ...(state?.showInvoiceAdModal || {}),
    },
    showInvoiceModuleOnboardingModal: {
      ...showInvoiceModuleOnboardingModalDefaultState,
      ...(state?.showInvoiceModuleOnboardingModal || {}),
    },
    showRentalsModal: state?.showRentalsModal,
  };
}

const vuexLocal = new VuexPersistence({
  reducer: (state) => ({
    auth: {
      token: state.auth.token,
      lang: state.auth.lang,
    },
    showProformModal: state.showProformModal,
    showZelleModal: state.showZelleModal,
    showCarouselModal: state.showCarouselModal,
    showInvoiceAdModal: state.showInvoiceAdModal,
    showInvoiceModuleOnboardingModal: state.showInvoiceModuleOnboardingModal,
    showRentalsModal: state.showRentalsModal,
  }),
  saveState: (key, state) => {
    try {
      const serialized = JSON.stringify(state);
      if (ENCRYPT_STORAGE && !ENCRYPTION_KEY) {
        console.warn(
          "[VuexPersist] ENCRYPT_STORAGE activo sin ENCRYPTION_KEY. No se persistira estado por seguridad."
        );
        return;
      }
      if (ENCRYPT_STORAGE) {
        const encrypted = CryptoJS.AES.encrypt(
          serialized,
          ENCRYPTION_KEY
        ).toString();
        window.localStorage.setItem(key, encrypted);
      } else {
        window.localStorage.setItem(key, serialized);
      }
    } catch (e) {
      console.error("[VuexPersist] Failed to save state:", e);
    }
  },
  restoreState: (key) => {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    try {
      if (ENCRYPT_STORAGE && !ENCRYPTION_KEY) {
        window.localStorage.removeItem(key);
        return {};
      }
      if (ENCRYPT_STORAGE) {
        const bytes = CryptoJS.AES.decrypt(raw, ENCRYPTION_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        if (!decrypted) {
          throw new Error("Decryption returned empty string");
        }
        return sanitizePersistedState(JSON.parse(decrypted));
      }
      return sanitizePersistedState(JSON.parse(raw));
    } catch (e) {
      console.warn(
        "[VuexPersist] Failed to restore state (key changed or data corrupted). Clearing storage."
      );
      window.localStorage.removeItem(key);
      return {};
    }
  },
});

export default new Vuex.Store({
  state: {
    loading: false,
    stateBaseLocationMap: null,
    stateOriginAddressMap: null,
    stateDestinationAddressMap: null,
    stateServiceTypeOrigin: null,
    stateTripComment: null,
    iconsDraggable: true,
    stateDriverCancelledEvent: false,
    stateDateTime: null,
    stateArrayMultiStop: [],
    stateMultiStopChangeInAddress: {
      changer: false,
      indexGiven: 0,
    },
    dateTimeError: null,
  },
  mutations: {
    SET_LOADING: (state, value) => (state.loading = value),
    SET_BASE: (state, value) => (state.stateBaseLocationMap = value),
    SET_ORIGIN: (state, value) => (state.stateOriginAddressMap = value),
    SET_DESTINATION: (state, value) =>
      (state.stateDestinationAddressMap = value),
    SET_SERVICETYPEORIGIN: (state, value) =>
      (state.stateServiceTypeOrigin = value),
    SET_DRAGGABLE: (state, value) => (state.iconsDraggable = value),
    SET_DRIVER_CANCELLED_EVENT: (state, value) =>
      (state.stateDriverCancelledEvent = value),
    SET_DATETIME: (state, value) => (state.stateDateTime = value),
    SET_STATE_ARRAY_MULTISTOP: (state, newArray) => {
      state.stateArrayMultiStop = [...newArray];
    },
    SET_STATE_MULTISTOP_CHANGE_IN_ADDRESS: (
      state,
      stateMultiStopChangeInAddress
    ) => {
      state.stateMultiStopChangeInAddress = {
        ...state.stateMultiStopChangeInAddress,
        ...stateMultiStopChangeInAddress,
      };
    },
    SET_STATE_TRIP_COMMENT: (state, value) => (state.stateTripComment = value),
    SET_DATE_TIME_ERROR: (state, value) => (state.dateTimeError = value),
  },
  actions: {
    setLoading({ commit }, payload) {
      commit("SET_LOADING", payload);
    },
    setBase({ commit }, payload) {
      commit("SET_BASE", payload);
    },
    setOrigin({ commit }, payload) {
      commit("SET_ORIGIN", payload);
    },
    setDestination({ commit }, payload) {
      commit("SET_DESTINATION", payload);
    },
    setServiceTypeOrigin({ commit }, payload) {
      commit("SET_SERVICETYPEORIGIN", payload);
    },
    setDraggable({ commit }, payload) {
      commit("SET_DRAGGABLE", payload);
    },
    setDriverCancelledEvent({ commit }, payload) {
      commit("SET_DRIVER_CANCELLED_EVENT", payload);
    },
    setDateTime({ commit }, payload) {
      commit("SET_DATETIME", payload);
    },
    setStateArrayMultiStop({ commit }, payload) {
      commit("SET_STATE_ARRAY_MULTISTOP", payload);
    },
    setStateMultiStopChangeInAddress({ commit }, payload) {
      commit("SET_STATE_MULTISTOP_CHANGE_IN_ADDRESS", payload);
    },
    setStateTripComment({ commit }, payload) {
      commit("SET_STATE_TRIP_COMMENT", payload);
    },
    setDateTimeError({ commit }, payload) {
      commit("SET_DATE_TIME_ERROR", payload);
    },
  },
  modules: {
    appConfig: appConfigStoreModule,
    app,
    auth,
    showProformModal,
    showZelleModal,
    showCarouselModal,
    showInvoiceAdModal,
    showInvoiceModuleOnboardingModal,
    showRentalsModal,
    dashboard,
    findYourTruck,
  },
  plugins: [vuexLocal.plugin],
});
